/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-delete-veh-detail',
    templateUrl: './delete-veh-detail.component.html',
    styleUrl: './delete-veh-detail.component.scss',
    standalone: false
})
export class DeleteVehDetailComponent {
  showSpinner: boolean;
  file: File;
  sheets: string[];
  sheetNames: string[];

  constructor(readonly vehicleScenarioService: VehicleScenarioService,
              readonly stateService: StateService,
              readonly excelService: ExcelExportService,
              public snackBar: FabMessageService,
              public dialog: MatDialog) {
  }

  /**
   * Exports the Vehicle records for the select working scenario
   */
  getVehicleRecords(): void {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileName = 'VehRecords_' + this.stateService.selectedScenario.vehScenId + "_" + currDate;

    this.showSpinner = true;
    this.vehicleScenarioService.getVehicleDetailRecordsForDelete(this.stateService.getUserGmid(), this.stateService.selectedScenario.vehScenId, this.stateService.selectedScenario.fcorg).subscribe({
      next: (data) => {
        this.excelService.exportToExcelFromServer(data, fileName);
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Successfully retrieved data. Exporting...', DISMISS_MSG);
      }
    });
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheetNames = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.sheetNames.length === 0) {
          this.file = null;
          this.snackBar.displayError("Please re-upload file", DISMISS_MSG)
        } else {
          this.openDeleteConfirmation();
        }
      }
    );
  }

  /**
   * Opens the delete confirmation to continue with deleting vehicle records
   */
  openDeleteConfirmation(): void {
    // Open confirmation dialog on what you are about to delete
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: 'This will delete vehicle records that were marked in the uploaded file. Continue?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    }).afterClosed().subscribe({
      next: confirmation => {
        if (confirmation) {
          this.validateVehDetailRecordsForDelete()
        }
      }
    });
  }

  /**
   * Validate the uploaded file that is used to delete vehicle records. If validation passes, proceed with deletion.
   */
  validateVehDetailRecordsForDelete(): void {
    this.showSpinner = true;
    this.vehicleScenarioService.validateVehDetailRecordsForDelete(this.file, this.sheetNames,
      this.stateService.selectedScenario.vehScenId, this.stateService.selectedScenario.fcorg).subscribe({
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.deleteVehRecords();
      }
    });
  }

  /**
   * Delete vehicle records that are marked in the uploaded file
   */
  deleteVehRecords(): void {
    this.showSpinner = true;
    this.vehicleScenarioService.deleteVehicleRecords(this.file, this.sheetNames, this.stateService.getUserGmid()).subscribe({
      next: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Any marked vehicle records have been successfully deleted', DISMISS_MSG);
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      }
    });
  }
}
